import TableActions from "../../BP2Components/Table/TableActions";
import { Button } from "../Button/Button";
import { Icon_Plus2 } from "../Icons/icons";
import moment from "moment";
import React from "react";
import Checkbox from "../Checkbox";

export class TableScripts {
   static prepareColumns = (
      columnList,
      filter,
      actions,
      onCustomEvent,
      addRecordBtn,
      addRecordCallback,
      selectRow,
      selectAllRows,
      rows,
      selectedRows,
   ) => {
      // safety: ensure rows is array
      const rowsArray = Array.isArray(rows) ? rows : [];

      // build id -> index map once for O(1) lookups in renderCell
      const rowIndexMap = rowsArray.reduce((acc, row, idx) => {
         // choose a stable id key (try common fields)
         const idKey = String(row.ID ?? row.id ?? row.mainRecordId ?? idx);
         acc[idKey] = idx;
         return acc;
      }, {});

      // columnList - list of {label: labelValue, ...} objects to create columns
      const preparedColumns = columnList.map((col, i) => {
         const isHidden = col?.options?.hidden === "1" || col?.options?.visible === false;
         const isActionColumn = col.label === "api_recordActions";
         const columnLabel = isActionColumn ? "Actions" : col.label;
         const selectEntries = !!col?.meta?.selectEntries?.length ? col?.meta?.selectEntries : null;
         const type = selectEntries ? "Select" : (col?.meta?.type ?? "");
         const isCheckboxColumn = col.techName === "checked";

         return {
            id: col.techName,
            techName: col?.techName ?? col?.id,
            label:
               addRecordBtn && i === 0 ? (
                  <Button
                     size="regular"
                     buttonType="primary"
                     style={{
                        width: 32,
                        border: "none",
                        position: "relative",
                        display: "flex",
                        padding: 0,
                     }}
                     onClick={addRecordCallback}
                  >
                     <Icon_Plus2 fill="var(--btn-primaryText)" />
                  </Button>
               ) : isActionColumn ? (
                  columnLabel
               ) : isCheckboxColumn ? (
                  <Checkbox
                     checked={rowsArray.every((row) => selectedRows.includes(row.ID))}
                     indeterminate={
                        rowsArray.some((row) => selectedRows.includes(row.ID)) &&
                        !rowsArray.every((row) => selectedRows.includes(row.ID))
                     }
                     onChange={(e) => selectAllRows(e.target.checked, rowsArray)}
                  />
               ) : (
                  <div className="column-header">
                     <div className="column-header__label">
                        {col.label.charAt(0).toUpperCase() + col.label.substring(1)}
                     </div>
                     {filter ? (
                        <div className="column-header__actions">
                           <div className="column-header__actions__filter">
                              {filter(col.techName ? col.techName : col.options.techName)}
                           </div>
                        </div>
                     ) : null}
                  </div>
               ),
            renderCell: (item) => {
               const cellValue = item[col.label] || "";
               if (isActionColumn) {
                  // compute idKey for this item the same way as for rowIndexMap
                  const idKey = String(item.ID ?? item.id ?? item.mainRecordId ?? "");
                  const rowIndex = rowIndexMap[idKey] ?? -1;
                  return (
                     <TableActions
                        actions={actions}
                        cellData={item}
                        onCustomEvent={onCustomEvent}
                        rowIndex={rowIndex}
                     />
                  );
               } else if (isCheckboxColumn) {
                  return (
                     <Checkbox
                        checked={selectedRows.includes(item.ID)}
                        onChange={(e) => selectRow(item, e.target.checked)}
                     />
                  );
               } else {
                  return <div style={{ wordBreak: "break-word" }}>{cellValue.toString()}</div>;
               }
            },
            dataKey: col.label,
            resize: true,
            hide: isHidden,
            type: type,
            dataUrl: selectEntries,
         };
      });
      return preparedColumns;
   };
   static prepareColumnsFromRows = (rowList) => {
      // rowList - List of {key: value} objects to create columns from it
      const preparedColumns = Object.keys(rowList[0]).map((key) => {
         return {
            label: key.charAt(0).toUpperCase() + key.substring(1),
            renderCell: (item) => item[key],
            /* renderCell: (item) => (
               <TextArea
                  value={item.name}
                  onChange={(event) => console.log(event.target.value, item.id, "name")}
               />
            ), */
            resize: true,
            hide: false,
         };
      });
      return preparedColumns;
   };
   static prepareRows = (rowList, intl, preparedFieldType) => {
      const fieldTypeMap = preparedFieldType.reduce((acc, field) => {
         acc[field.label] = field.type;
         return acc;
      }, {});
      rowList.forEach((row) => {
         Object.keys(row).forEach((key) => {
            if (key === "api_recordActions") {
            } else if (key === "api_instanceStatus" || key === "api_performerStatus") {
               row[key] = "";
            } else {
               if (row[key] !== undefined && typeof row[key] === "boolean") {
                  if (row[key]) {
                     row[key] = intl.formatMessage({ id: "global.yes" });
                  } else {
                     row[key] = intl.formatMessage({ id: "global.no" });
                  }
               }
               if (row[key] && typeof row[key] !== "string" && typeof row[key] !== "number") {
                  if (Array.isArray(row[key])) {
                     row[key] = row[key].join(", ");
                  }
                  if (
                     row[key]["value"] &&
                     typeof row[key]["value"] === "object" &&
                     !Array.isArray(row[key])
                  ) {
                     row[key] = row[key]["value"];
                  }
               }
               if (row[key] && fieldTypeMap[key] === "LocalDateTime") {
                  row[key] = moment(row[key]).format("YYYY-MM-DD HH:mm");
               }
            }
         });
      });
      // rowList - list of {key: value} objects. React tables needs { [ {}, {}, ... ] } format
      return { nodes: rowList };
   };
   static prepareTableData = (
      colData,
      rowData,
      filter,
      actions,
      onCustomEvent,
      addRecordBtn,
      intl,
      addRecordCallback,
      withCheckbox,
      selectRow,
      selectAllRows,
      selectedRows,
   ) => {
      // colData - list of {label: labelValue, ...} objects
      // rowData - list of {key: value} objects
      const preparedFieldType = (colData ?? []).map((col) => ({
         label: col.label,
         type: col.meta?.type ?? "",
      }));
      const hasActions = Array.isArray(actions)
         ? actions.length > 0
         : actions && Object.keys(actions).length > 0;
      if (hasActions) {
         colData = [
            {
               techName: "api_recordActions",
               label: "api_recordActions",
               options: { visible: true },
            },
            ...colData,
         ];
      }
      if (withCheckbox) {
         const checkBoxCol = {
            techName: "checked",
            label: "",
            options: { visible: true },
         };
         if (!!actions && (!!actions.length || !!Object.keys(actions)?.length)) {
            colData.splice(1, 0, checkBoxCol);
         } else {
            colData = [checkBoxCol, ...colData];
         }
      }
      let preparedColumns = [];
      let preparedRows = [];
      if (colData && rowData) {
         preparedColumns = this.prepareColumns(
            colData,
            filter,
            actions,
            onCustomEvent,
            addRecordBtn,
            addRecordCallback,
            selectRow,
            selectAllRows,
            rowData,
            selectedRows,
         );
         preparedRows = this.prepareRows(rowData, intl, preparedFieldType);
      } else if (rowData) {
         preparedColumns = this.prepareColumnsFromRows(rowData);
         preparedRows = this.prepareRows(rowData, intl, preparedFieldType);
      }
      return { preparedColumns, preparedRows };
   };
   static getColumnWidths = (columns) => {
      let columnWidths = "";
      columns.forEach((col, i) => {
         if (col.hide === false) {
            if (i !== columns.length - 1) {
               columnWidths += "minmax(min-content, auto) ";
            } else {
               columnWidths += "1fr ";
            }
         }
      });
      return columnWidths;
   };
}
